import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiFilter,
  FiCalendar,
  FiUser,
  FiEdit,
  FiX,
  FiTrendingUp
} from 'react-icons/fi';
import membershipService from '../services/membership.service';
import { formatDate } from '../utils/dateUtils';
import DateInput from '../components/DateInput';

const MembershipList = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeOptions, setUpgradeOptions] = useState({});
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    member_id: '',
    plan_id: ''
  });

  useEffect(() => {
    // Allow refetch when dependencies change, but prevent initial double-fetch
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchMemberships();
    } else {
      fetchMemberships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        ),
      };

      const response = await membershipService.getAllMemberships(params);
      setMemberships(response.data?.memberships || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
      setTotal(response.data?.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to fetch memberships');
      console.error('Error fetching memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      member_id: '',
      plan_id: ''
    });
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      frozen: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
        {paymentStatus?.charAt(0).toUpperCase() + paymentStatus?.slice(1)}
      </span>
    );
  };

  const canUpdateStartDate = (membership) => {
    const startDate = new Date(membership.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return startDate >= today;
  };

  const canUpgradePlan = (membership) => {
    return membership.status === 'active';
  };

  const handleUpdateStartDate = (membership) => {
    setSelectedMembership(membership);
    setNewStartDate(membership.start_date);
    setShowUpdateModal(true);
  };

  const submitUpdateStartDate = async () => {
    if (!newStartDate) {
      toast.error('Please select a start date');
      return;
    }

    try {
      await membershipService.updateStartDate(selectedMembership.id, newStartDate);
      toast.success('Start date updated successfully');
      setShowUpdateModal(false);
      fetchMemberships();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update start date';
      toast.error(errorMessage);
    }
  };

  const handleUpgradePlan = async (membership) => {
    try {
      setLoadingUpgrade(true);
      const response = await membershipService.getUpgradeOptions(membership.member_id);
      setUpgradeOptions(response.data || {});
      setSelectedMembership(membership);
      setShowUpgradeModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load upgrade options';
      toast.error(errorMessage);
    } finally {
      setLoadingUpgrade(false);
    }
  };

  const handleSelectUpgrade = (plan) => {
    navigate('/membership/purchase', {
      state: {
        memberId: selectedMembership.member_id,
        plan: plan,
        isUpgrade: true,
        currentMembershipId: selectedMembership.id,
        currentPlanPrice: upgradeOptions.current_membership?.plan_price || 0,  // Current plan's price
        currentPlanName: upgradeOptions.current_membership?.plan_name || ''
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Memberships</h1>
          <p className="text-gray-600 mt-1">Manage all active and past memberships</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiFilter className="mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="frozen">Frozen</option>
              </select>
            </div>

            <div className="md:col-span-3 flex justify-end">
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow p-4">
        <p className="text-sm text-gray-600">
          Showing {memberships.length} of {total} memberships
        </p>
      </div>

      {/* Memberships Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : memberships.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No memberships found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {memberships.map((membership) => (
                  <tr key={membership.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {membership.first_name} {membership.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {membership.member_code}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{membership.plan_name}</div>
                      <div className="text-sm text-gray-500">{membership.duration_days} days</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(membership.start_date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(membership.end_date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">₹{parseFloat(membership.amount_paid).toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(membership.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(membership.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/members/${membership.member_id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Member"
                        >
                          <FiUser size={18} />
                        </button>
                        {canUpdateStartDate(membership) && (
                          <button
                            onClick={() => handleUpdateStartDate(membership)}
                            className="text-green-600 hover:text-green-900"
                            title="Update Start Date"
                          >
                            <FiCalendar size={18} />
                          </button>
                        )}
                        {canUpgradePlan(membership) && (
                          <button
                            onClick={() => handleUpgradePlan(membership)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Upgrade Plan"
                            disabled={loadingUpgrade}
                          >
                            <FiTrendingUp size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>

            <div className="flex items-center space-x-2">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-lg ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return <span key={page}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Update Start Date Modal */}
      {showUpdateModal && selectedMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Update Start Date</h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Member: {selectedMembership.first_name} {selectedMembership.last_name}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Plan: {selectedMembership.plan_name}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Start Date
              </label>
              <DateInput
                name="new_start_date"
                value={newStartDate}
                onChange={(value) => setNewStartDate(value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Enter date as: DD/MM/YYYY (e.g., 28/12/2025)</p>
              <p className="text-xs text-gray-500 mt-2">
                Current start date: {formatDate(selectedMembership.start_date)}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitUpdateStartDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && selectedMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Upgrade Membership Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Member: {selectedMembership.first_name} {selectedMembership.last_name}
              </p>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Current Plan: {upgradeOptions.current_membership?.plan_name}
              </p>
              <p className="text-xs text-gray-500">
                Price: ₹{upgradeOptions.current_membership?.plan_price} |
                Expires: {formatDate(upgradeOptions.current_membership?.end_date)}
              </p>
            </div>

            {upgradeOptions.available_upgrades && upgradeOptions.available_upgrades.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Available Upgrades:</h4>
                {upgradeOptions.available_upgrades.map((plan) => (
                  <div
                    key={plan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-500 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleSelectUpgrade(plan)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h5 className="font-bold text-gray-900">{plan.name}</h5>
                        <p className="text-sm text-gray-600">{plan.duration_days} days</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-600">
                          +₹{plan.upgrade_details.upgrade_cost}
                        </p>
                        <p className="text-xs text-gray-500">Upgrade Cost</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>New Plan Price: ₹{plan.final_price}</span>
                      <span className="text-purple-600 font-medium">Click to upgrade</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No higher-tier plans available for upgrade</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipList;
